import type { CreateSetup, HVFExtrema } from '@here/api'
import { Button, Field, Heading, Text } from '@here/components'
import { createForm, getValues, setValue, valiForm } from '@modular-forms/solid'
import 'cropperjs/dist/cropper.min.css'
import { For, Show, createEffect } from 'solid-js'
import { css } from 'styled-system/css'
import { Box, Divider, Grid } from 'styled-system/jsx'
import * as v from 'valibot'
import type { ScanedDetails } from './scannedDetails'

export type GeneralInformation = Pick<CreateSetup, 'symbol' | 'exchange'>

export type HVFExtremaNumberOnly = {
	[K in keyof HVFExtrema]: number | null
}

const HVFExtremaSchema: v.GenericSchema<ScanedDetails> = v.object({
	symbol: v.string(),
	exchange: v.string(),
	high_1_price: v.number(),
	high_1_time: v.number(),
	high_2_price: v.number(),
	high_2_time: v.number(),
	high_3_price: v.number(),
	high_3_time: v.number(),
	low_1_price: v.number(),
	low_1_time: v.number(),
	low_2_price: v.number(),
	low_2_time: v.number(),
	low_3_price: v.number(),
	low_3_time: v.number(),
})

export const Confirm = (props: {
	preview?: string
	onConfirmDetails: (values: ScanedDetails) => void
	isDisabled: boolean
	details: ScanedDetails
}) => {
	const [form, { Form, Field: FormField }] = createForm<ScanedDetails>({
		validate: valiForm(HVFExtremaSchema),
	})

	const fields: Array<keyof HVFExtrema> = [
		'high_1_price',
		'high_1_time',
		'low_1_price',
		'low_1_time',
		'high_2_price',
		'high_2_time',
		'low_2_price',
		'low_2_time',
		'high_3_price',
		'high_3_time',
		'low_3_price',
		'low_3_time',
	]

	createEffect(() => {
		setValue(form, 'symbol', props.details.symbol)
		setValue(form, 'exchange', props.details.exchange)
		setValue(form, 'high_1_price', props.details.high_1_price)
		setValue(form, 'high_1_price', props.details.high_1_price)
		setValue(form, 'high_1_time', props.details.high_1_time)
		setValue(form, 'high_2_price', props.details.high_2_price)
		setValue(form, 'high_2_time', props.details.high_2_time)
		setValue(form, 'high_3_price', props.details.high_3_price)
		setValue(form, 'high_3_time', props.details.high_3_time)
		setValue(form, 'low_1_price', props.details.low_1_price)
		setValue(form, 'low_1_time', props.details.low_1_time)
		setValue(form, 'low_2_price', props.details.low_2_price)
		setValue(form, 'low_2_time', props.details.low_2_time)
		setValue(form, 'low_3_price', props.details.low_3_price)
		setValue(form, 'low_3_time', props.details.low_3_time)
	})

	return (
		<Grid gap={2}>
			<Divider />
			<Text children="Double check the image details were correctly scanned before confirming" />
			<Grid
				class={css({
					gridTemplateColumns: {
						base: '1fr 1fr',
						lgDown: '1fr',
					},
					gridTemplateRows: {
						lgDown: 'auto 1fr',
					},
				})}
			>
				<Show when={props.preview}>
					<img
						src={props.preview}
						alt="Cropped preview"
						class={css({ width: '100%' })}
					/>
				</Show>
				<Grid alignContent="start">
					<Show when={props.isDisabled}>
						<FormField name="symbol">
							{(field, formFieldProps) => (
								<Field.Root invalid={!!field.error}>
									<Field.Label>Symbol</Field.Label>
									<Field.Input
										type="text"
										{...formFieldProps}
										value={field.value as any}
									/>
									<Field.ErrorText>
										{field.error && <div>{field.error}</div>}
									</Field.ErrorText>
								</Field.Root>
							)}
						</FormField>

						<FormField name="exchange">
							{(field, formFieldProps) => (
								<Field.Root invalid={!!field.error}>
									<Field.Label>Exchange</Field.Label>
									<Field.Input
										type="text"
										{...formFieldProps}
										value={field.value as any}
									/>
									<Field.ErrorText>
										{field.error && <div>{field.error}</div>}
									</Field.ErrorText>
								</Field.Root>
							)}
						</FormField>

						<Grid gridTemplateColumns="1fr 2fr">
							<Heading size="xs">Price</Heading>
							<Heading size="xs">Time</Heading>

							<For each={fields}>
								{(formField) => (
									<FormField
										name={formField}
										type={formField.includes('time') ? 'string' : 'number'}
									>
										{(field, formFieldProps) => (
											<>
												<Field.Root invalid={!!field.error}>
													<Field.Label>{formField}</Field.Label>
													<Field.Input
														{...formFieldProps}
														value={field.value as any}
														type={'number'}
														onChange={(event) => {
															let number: number | undefined = Number(
																event.target.value,
															)
															number = isNaN(number) ? undefined : number
															setValue(form, formField, number)
														}}
													/>
													<Field.ErrorText>{<div>{field.error}</div>}</Field.ErrorText>
												</Field.Root>
											</>
										)}
									</FormField>
								)}
							</For>
						</Grid>

						<Button
							colorPalette="tscGreen"
							onClick={() => props.onConfirmDetails(getValues(form) as ScanedDetails)}
						>
							Confirm Details
						</Button>
					</Show>
				</Grid>
			</Grid>
		</Grid>
	)
}
